import React from "react";
import { motion } from "framer-motion";

/**
 * Hero background letters — confined to "safe zones" that never overlap
 * with the navigation, the main copy column, the hero headline, or the
 * hero video card. Right-margin and bottom-band letters are hidden on
 * narrow viewports where the video and copy span the full container.
 *
 * Safe zones (in % of section box):
 *   - Left margin strip:   x 0%–12%,  y 22%–95%   (always visible)
 *   - Right margin strip:  x 94%–99%, y 22%–95%   (xl+ only — outside video card)
 *   - Bottom band:         x 26%–60%, y 84%–96%   (lg+ only — below copy column)
 */
const LEFT = [
  { ch: "K", color: "#0F766E", x: "1%",  y: "22%", size: 64, dur: 7 },
  { ch: "m", color: "#EA580C", x: "7%",  y: "28%", size: 42, dur: 6 },
  { ch: "★", color: "#EAB308", x: "11%", y: "24%", size: 24, dur: 4 },
  { ch: "b", color: "#C026D3", x: "3%",  y: "36%", size: 48, dur: 6 },
  { ch: "✦", color: "#0F766E", x: "9%",  y: "42%", size: 20, dur: 5 },
  { ch: "Q", color: "#4A1D52", x: "5%",  y: "48%", size: 58, dur: 7 },
  { ch: "e", color: "#16A34A", x: "10%", y: "54%", size: 38, dur: 6 },
  { ch: "R", color: "#DC2626", x: "2%",  y: "60%", size: 52, dur: 6 },
  { ch: "★", color: "#C026D3", x: "8%",  y: "66%", size: 22, dur: 4 },
  { ch: "a", color: "#16A34A", x: "4%",  y: "72%", size: 44, dur: 6 },
  { ch: "y", color: "#EAB308", x: "9%",  y: "78%", size: 40, dur: 7 },
  { ch: "✦", color: "#EA580C", x: "3%",  y: "84%", size: 20, dur: 5 },
  { ch: "g", color: "#0F766E", x: "7%",  y: "90%", size: 42, dur: 7 },
];

const RIGHT = [
  { ch: "Q", color: "#C026D3", x: "94%", y: "22%", size: 60, dur: 7 },
  { ch: "★", color: "#EAB308", x: "98%", y: "28%", size: 24, dur: 4 },
  { ch: "z", color: "#16A34A", x: "95%", y: "34%", size: 44, dur: 7 },
  { ch: "✦", color: "#C026D3", x: "98%", y: "40%", size: 20, dur: 5 },
  { ch: "L", color: "#EA580C", x: "94%", y: "46%", size: 52, dur: 6 },
  { ch: "e", color: "#0F766E", x: "98%", y: "52%", size: 38, dur: 6 },
  { ch: "★", color: "#DC2626", x: "95%", y: "58%", size: 22, dur: 4 },
  { ch: "y", color: "#EAB308", x: "97%", y: "64%", size: 42, dur: 7 },
  { ch: "B", color: "#4A1D52", x: "94%", y: "72%", size: 50, dur: 7 },
  { ch: "✦", color: "#16A34A", x: "98%", y: "78%", size: 20, dur: 5 },
  { ch: "g", color: "#C026D3", x: "95%", y: "84%", size: 42, dur: 6 },
  { ch: "★", color: "#EAB308", x: "98%", y: "90%", size: 24, dur: 4 },
];

const BOTTOM = [
  { ch: "B", color: "#DC2626", x: "28%", y: "86%", size: 44, dur: 7 },
  { ch: "★", color: "#EAB308", x: "34%", y: "92%", size: 22, dur: 4 },
  { ch: "y", color: "#0F766E", x: "40%", y: "88%", size: 40, dur: 6 },
  { ch: "✦", color: "#C026D3", x: "46%", y: "94%", size: 20, dur: 5 },
  { ch: "a", color: "#EA580C", x: "52%", y: "86%", size: 42, dur: 6 },
  { ch: "★", color: "#16A34A", x: "58%", y: "92%", size: 22, dur: 4 },
];

function LetterSpan({ l, i }) {
  const delay = (i % 9) * 0.18;
  const drift = 10 + (i % 5) * 3;
  const rot = (i % 2 === 0 ? 1 : -1) * (3 + (i % 5));
  return (
    <motion.span
      initial={{ opacity: 0, y: 18 }}
      animate={{
        opacity: [0.65, 0.95, 0.7],
        y: [0, -drift, 0],
        rotate: [-rot, rot, -rot],
      }}
      transition={{
        opacity: { delay, duration: l.dur, repeat: Infinity, ease: "easeInOut" },
        y: { delay, duration: l.dur, repeat: Infinity, ease: "easeInOut" },
        rotate: { delay, duration: l.dur + 1, repeat: Infinity, ease: "easeInOut" },
      }}
      style={{
        position: "absolute",
        left: l.x,
        top: l.y,
        color: l.color,
        fontFamily: "Outfit, sans-serif",
        fontWeight: 800,
        fontSize: `${l.size}px`,
        lineHeight: 1,
        filter: "drop-shadow(0 6px 14px rgba(74,29,82,0.10))",
        willChange: "transform, opacity",
      }}
    >
      {l.ch}
    </motion.span>
  );
}

export default function FloatingLetters() {
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Left strip — hidden on mobile/tablet (would overlap copy), visible on md+ */}
      <div className="absolute inset-0 hidden md:block">
        {LEFT.map((l, i) => <LetterSpan key={`l-${i}`} l={l} i={i} />)}
      </div>
      {/* Right strip — only on xl+ (where there's space outside the video card) */}
      <div className="absolute inset-0 hidden xl:block">
        {RIGHT.map((l, i) => <LetterSpan key={`r-${i}`} l={l} i={i + 13} />)}
      </div>
      {/* Bottom band — only on lg+ (where the copy column doesn't fill width) */}
      <div className="absolute inset-0 hidden lg:block">
        {BOTTOM.map((l, i) => <LetterSpan key={`b-${i}`} l={l} i={i + 25} />)}
      </div>
    </div>
  );
}
